import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { rechargeAPI, walletAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import BannerSlider from '../components/BannerSlider';
import {
  Zap, Loader2, Search, Flame, Wind, FileText, Receipt,
  Wallet, ArrowRight, CheckCircle, XCircle, AlertTriangle, User,
} from 'lucide-react';

const SERVICE_META = {
  electricity: { label: 'Electricity Bill', icon: Zap, color: '#eab308', gradient: 'from-amber-500 via-yellow-500 to-orange-400' },
  gas: { label: 'Gas Bill', icon: Flame, color: '#f97316', gradient: 'from-orange-500 via-red-400 to-pink-500' },
  fastag: { label: 'FASTag Recharge', icon: Receipt, color: '#ef4444', gradient: 'from-red-500 via-rose-500 to-pink-500' },
  loan: { label: 'Loan Payment', icon: FileText, color: '#14b8a6', gradient: 'from-teal-500 via-emerald-500 to-green-400' },
  water: { label: 'Water Bill', icon: Wind, color: '#06b6d4', gradient: 'from-cyan-500 via-teal-400 to-emerald-400' },
  insurance: { label: 'Insurance', icon: FileText, color: '#8b5cf6', gradient: 'from-violet-500 via-purple-500 to-blue-500' },
};

export default function BillPayment() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const serviceType = searchParams.get('type') || 'electricity';
  const meta = SERVICE_META[serviceType] || SERVICE_META.electricity;
  const Icon = meta.icon;

  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOp, setSelectedOp] = useState(null);
  const [number, setNumber] = useState('');
  const [field1, setField1] = useState('');
  const [field2, setField2] = useState('');
  const [fetching, setFetching] = useState(false);
  const [billData, setBillData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const [paying, setPaying] = useState(false);
  const [payResult, setPayResult] = useState(null);

  useEffect(() => {
    fetchOperators();
  }, [serviceType]);

  const fetchOperators = async () => {
    setLoading(true);
    try {
      const r = await rechargeAPI.getBillOperators(serviceType);
      setOperators(Array.isArray(r.data) ? r.data : (r.data?.operators || []));
    } catch {
      toast.error('Failed to load operators');
    } finally { setLoading(false); }
  };

  const handleOperatorChange = (opCode) => {
    const op = operators.find(o => o.operator_code === opCode);
    setSelectedOp(op || null);
    setField1('');
    setField2('');
    setBillData(null);
  };

  const handleFetchBill = async () => {
    if (!number) { toast.error('Enter service number'); return; }
    if (!selectedOp) { toast.error('Select an operator'); return; }
    if (selectedOp.has_field1 && !field1) { toast.error(`Select ${selectedOp.field1_label}`); return; }

    setFetching(true);
    setBillData(null);
    try {
      const r = await rechargeAPI.fetchBill({
        number,
        operator_code: selectedOp.operator_code,
        field1: field1 || '',
        field2: field2 || '',
      });
      if (r.data?.status === 'success') {
        setBillData(r.data);
        setShowDetails(true);
      } else {
        toast.error(r.data?.message || 'Bill fetch failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bill fetch failed');
    } finally { setFetching(false); }
  };

  const getDataValue = (label) => {
    const item = billData?.data?.find(d => d.label === label);
    return item?.value || '—';
  };

  const handlePayBill = async () => {
    if (!billData || !selectedOp) return;
    const amt = parseFloat(billData.amount);
    if (amt <= 0) { toast.error('Invalid bill amount'); return; }
    if (amt > parseFloat(user?.wallet_balance || 0)) { toast.error('Insufficient wallet balance'); return; }

    setPaying(true);
    setPayResult(null);
    try {
      const r = await rechargeAPI.payBill({
        mobile: number,
        operator: selectedOp.operator_code,
        amount: amt,
        circle: 'All India',
        recharge_type: serviceType,
      });
      const status = r.data?.status;
      setPayResult(r.data);
      if (status === 'success') {
        toast.success('Payment successful!');
      } else if (status === 'pending') {
        toast.info('Payment is processing. Check status in Recharge History.');
      } else {
        toast.error('Payment failed: ' + (r.data?.api_response || 'Unknown error'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally { setPaying(false); }
  };

  const handlePrintReceipt = () => {
    if (!payResult || !billData) return;
    const w = window.open('', '_blank', 'width=400,height=600');
    const customerName = billData.data?.find(d => d.label === 'CustomerName')?.value || '-';
    const billNumber = billData.data?.find(d => d.label === 'BillNumber')?.value || '-';
    const billDate = billData.data?.find(d => d.label === 'BillDate')?.value || '-';
    const dueDate = billData.data?.find(d => d.label === 'DueDate')?.value || '-';
    const billPeriod = billData.data?.find(d => d.label === 'BillPeriod')?.value || '-';
    w.document.write(`<!DOCTYPE html><html><head><title>Payment Receipt</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',sans-serif;padding:20px;color:#1e293b;max-width:380px;margin:0 auto}
.header{text-align:center;border-bottom:2px solid #4680ff;padding-bottom:12px;margin-bottom:16px}
.logo{font-size:18px;font-weight:800;color:#4680ff}
.subtitle{font-size:10px;color:#94a3b8;margin-top:2px}
.status{display:inline-block;padding:4px 16px;border-radius:20px;font-size:11px;font-weight:700;margin:10px 0;
  ${payResult.status === 'success' ? 'background:#dcfce7;color:#16a34a' : payResult.status === 'pending' ? 'background:#fef3c7;color:#d97706' : 'background:#fce4ec;color:#dc2626'}}
.row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #e2e8f0;font-size:12px}
.row .label{color:#64748b}.row .value{font-weight:600;text-align:right}
.amount-row{font-size:16px;font-weight:800;color:#16a34a;border-bottom:2px solid #e2e8f0;padding:10px 0}
.footer{text-align:center;margin-top:20px;font-size:9px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:10px}
@media print{.no-print{display:none}}
</style></head><body>
<div class="header">
  <div class="logo">TRIPATHY TELECOM</div>
  <div class="subtitle">Bill Payment Receipt</div>
</div>
<div style="text-align:center"><span class="status">${payResult.status === 'success' ? 'PAYMENT SUCCESSFUL' : payResult.status === 'pending' ? 'PROCESSING' : 'PAYMENT FAILED'}</span></div>
<div class="row amount-row"><span>Amount Paid</span><span>₹${billData.amount}</span></div>
<div class="row"><span class="label">Service</span><span class="value">${meta.label}</span></div>
<div class="row"><span class="label">Provider</span><span class="value">${selectedOp?.operator_name || '-'}</span></div>
<div class="row"><span class="label">Consumer No.</span><span class="value">${number}</span></div>
<div class="row"><span class="label">Customer</span><span class="value">${customerName}</span></div>
<div class="row"><span class="label">Bill Number</span><span class="value">${billNumber}</span></div>
<div class="row"><span class="label">Bill Date</span><span class="value">${billDate}</span></div>
<div class="row"><span class="label">Bill Period</span><span class="value">${billPeriod}</span></div>
<div class="row"><span class="label">Due Date</span><span class="value">${dueDate}</span></div>
<div class="row"><span class="label">Transaction ID</span><span class="value">${payResult.txn_id || '-'}</span></div>
<div class="row"><span class="label">Date & Time</span><span class="value">${new Date().toLocaleString('en-IN')}</span></div>
<div class="row"><span class="label">Paid By</span><span class="value">${user?.name || '-'}</span></div>
<div class="footer">
  <p>Thank you for using Tripathy Telecom</p>
  <p style="margin-top:4px">For support: tripathytelecom.shop</p>
</div>
<div class="no-print" style="text-align:center;margin-top:16px">
  <button onclick="window.print()" style="padding:8px 24px;background:#4680ff;color:white;border:none;border-radius:8px;cursor:pointer;font-weight:600">Print Receipt</button>
</div>
</body></html>`);
    w.document.close();
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="bill-payment-page">

        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-3xl mb-6" style={{
          background: `linear-gradient(135deg, #1e1b4b 0%, #312e81 25%, #4338ca 50%, #6366f1 75%, #818cf8 100%)`,
          backgroundSize: '300% 300%',
          animation: 'gradientShift 8s ease infinite',
        }}>
          <div className="rch-orb animate-orb1" style={{ width: 120, height: 120, background: 'rgba(234,179,8,0.15)', top: -20, right: '20%' }} />
          <div className="rch-orb animate-orb2" style={{ width: 80, height: 80, background: 'rgba(249,115,22,0.12)', bottom: -10, left: '15%' }} />

          <div className="relative z-10 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold text-white/40 uppercase tracking-[3px] mb-1">Bill Payment</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">{meta.label}</h1>
              <p className="text-white/50 text-xs mt-1">Fetch and pay your bills instantly</p>
            </div>
            <div className="flex gap-3">
              <div className="px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10">
                <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Balance</p>
                <p className="text-xl font-extrabold text-white font-mono">&#8377;{parseFloat(user?.wallet_balance || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Banner Slider */}
        <BannerSlider pageType={serviceType} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-slate-100 p-6 sm:p-8">

            {/* Operator Select */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                Service Provider
              </label>
              {loading ? (
                <div className="h-12 rounded-xl bg-slate-50 animate-pulse" />
              ) : operators.length > 0 ? (
                <Select onValueChange={handleOperatorChange} data-testid="bill-operator-select">
                  <SelectTrigger className="h-12 rounded-xl border-slate-200 text-sm" data-testid="bill-operator-trigger">
                    <SelectValue placeholder="Choose operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map(op => (
                      <SelectItem key={op.id} value={op.operator_code} data-testid={`bill-op-${op.operator_code}`}>
                        {op.operator_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-12 rounded-xl bg-slate-50 flex items-center justify-center text-sm text-slate-400">
                  No operators available for this service
                </div>
              )}
            </div>

            {/* Service Number */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                <Receipt className="w-3.5 h-3.5 text-slate-400" />
                Service Number / Consumer Number
              </label>
              <Input
                placeholder="Enter service/consumer number"
                value={number}
                onChange={e => setNumber(e.target.value)}
                className="h-12 rounded-xl border-slate-200 text-sm font-mono"
                data-testid="bill-number-input"
              />
            </div>

            {/* Field 1 (e.g., Sub Division) */}
            {!!selectedOp?.has_field1 && (
              <div className="mb-6">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                  {selectedOp.field1_label || 'Field 1'}
                </label>
                {selectedOp.field1_options && selectedOp.field1_options.length > 0 ? (
                  <Select onValueChange={setField1} data-testid="bill-field1-select">
                    <SelectTrigger className="h-12 rounded-xl border-slate-200 text-sm" data-testid="bill-field1-trigger">
                      <SelectValue placeholder={`Select ${selectedOp.field1_label}`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {selectedOp.field1_options.map(opt => (
                        <SelectItem key={opt.code} value={opt.code} data-testid={`field1-${opt.code}`}>
                          {opt.code} — {opt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder={`Enter ${selectedOp.field1_label}`}
                    value={field1}
                    onChange={e => setField1(e.target.value)}
                    className="h-12 rounded-xl border-slate-200 text-sm"
                    data-testid="bill-field1-input"
                  />
                )}
              </div>
            )}

            {/* Field 2 */}
            {!!selectedOp?.has_field2 && (
              <div className="mb-6">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {selectedOp.field2_label || 'Field 2'}
                </label>
                {selectedOp.field2_options && selectedOp.field2_options.length > 0 ? (
                  <Select onValueChange={setField2} data-testid="bill-field2-select">
                    <SelectTrigger className="h-12 rounded-xl border-slate-200 text-sm">
                      <SelectValue placeholder={`Select ${selectedOp.field2_label}`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {selectedOp.field2_options.map(opt => (
                        <SelectItem key={opt.code} value={opt.code}>{opt.code} — {opt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder={`Enter ${selectedOp.field2_label}`}
                    value={field2}
                    onChange={e => setField2(e.target.value)}
                    className="h-12 rounded-xl border-slate-200 text-sm"
                  />
                )}
              </div>
            )}

            {/* Fetch Bill Button */}
            <button
              onClick={handleFetchBill}
              disabled={fetching || !selectedOp || !number}
              className="w-full h-14 rounded-xl text-white font-bold text-sm tracking-wide transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed rch-btn-gradient flex items-center justify-center gap-2"
              data-testid="fetch-bill-btn"
            >
              {fetching ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Fetching Bill...</>
              ) : (
                <><Search className="w-5 h-5" /> Fetch Bill</>
              )}
            </button>

            {/* Inline Bill Details */}
            {billData && showDetails && (
              <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-teal-50/50 overflow-hidden" data-testid="bill-details-inline">
                {/* Amount Header */}
                <div className="flex items-center justify-between px-5 py-4 bg-white/60 border-b border-emerald-100">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                      <Receipt className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">Bill Amount</p>
                      <p className="text-2xl font-extrabold font-mono text-emerald-700">&#8377;{billData.amount}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {billData.autoamount === 'yes' && (
                      <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[9px]">Auto Amount</Badge>
                    )}
                    <button onClick={() => { setBillData(null); setShowDetails(false); }} className="text-[10px] text-slate-400 hover:text-red-400 transition-colors">
                      Clear
                    </button>
                  </div>
                </div>

                {/* Compact Info Grid */}
                <div className="px-5 py-3 grid grid-cols-2 gap-x-4 gap-y-2">
                  {(billData.data || []).map((item, i) => (
                    <div key={i} className="flex flex-col py-1.5 border-b border-emerald-100/50 last:border-0">
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">{item.label.replace(/([A-Z])/g, ' $1').trim()}</span>
                      <span className="text-xs font-bold text-slate-700 font-mono truncate">{item.value || '—'}</span>
                    </div>
                  ))}
                </div>

                {/* Pay Button */}
                <div className="px-5 py-4 border-t border-emerald-100 bg-white/40">
                  {!payResult ? (
                    <button
                      onClick={handlePayBill}
                      disabled={paying}
                      className="w-full h-12 rounded-xl text-white font-bold text-sm tracking-wide rch-btn-gradient flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="pay-bill-btn"
                    >
                      {paying ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                      ) : (
                        <><Wallet className="w-4 h-4" /> Pay &#8377;{billData.amount}</>
                      )}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className={`w-full h-12 rounded-xl flex items-center justify-center gap-2 text-sm font-bold ${payResult.status === 'success' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : payResult.status === 'pending' ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                        {payResult.status === 'success' ? <CheckCircle className="w-4 h-4" /> : payResult.status === 'pending' ? <AlertTriangle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {payResult.status === 'success' ? 'Payment Successful' : payResult.status === 'pending' ? 'Processing...' : 'Payment Failed'}
                      </div>
                      {payResult.txn_id && (
                        <p className="text-[10px] text-center text-slate-400">TXN: {payResult.txn_id}</p>
                      )}
                      {(payResult.status === 'success' || payResult.status === 'pending') && (
                        <button
                          onClick={handlePrintReceipt}
                          className="w-full h-10 rounded-xl bg-blue-50 text-blue-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-100 transition border border-blue-200"
                          data-testid="print-receipt-btn"
                        >
                          <FileText className="w-4 h-4" /> Print Receipt
                        </button>
                      )}
                      {payResult.status === 'failed' && (
                        <button
                          onClick={() => { setPayResult(null); }}
                          className="w-full h-10 rounded-xl bg-slate-50 text-slate-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-slate-100 transition border border-slate-200"
                        >
                          <ArrowRight className="w-4 h-4" /> Retry Payment
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Info Sidebar */}
          <div className="space-y-4">
            {/* Quick Info Card */}
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">How it works</h3>
              <div className="space-y-3">
                {[
                  { step: '1', text: 'Select your service provider' },
                  { step: '2', text: 'Enter consumer/service number' },
                  { step: '3', text: 'Select sub-division if required' },
                  { step: '4', text: 'Click Fetch Bill to view details' },
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-[#4680ff]">{s.step}</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
